import {
  B2bAdminRoles,
  B2bAdminStatus,
  B2bAdminsLanguagePreference,
} from '@Common/types/bo/b2b/admins';
import {
  B2bCompanyFlag,
  KybStatus,
  PaymentMethodIntent,
  PaymentMethodPaymentType,
  PaymentMethodStatus,
  PaymentMethodType,
} from '@Common/types/bo/b2b/companies';
import { EmployeeStatus } from '@Common/types/bo/b2b/employees_properties';
import { MdlrBankNameFailedTaskRetryReason } from '@Common/types/bo/tasks/mdlr_bank_name_failed';

import { b2bCompaniesSavingPlansTranslations } from '@sh4res/bo-common/i18n/en/b2b/companies_saving_plans';

export const b2bCompaniesTranslations = {
  choices: {
    kyb_status: {
      created: 'Created',
      in_review: 'Under review',
      verified: 'KYB Completed',
      denied: 'Denied',
      resigned: 'Resigned',
    } satisfies Record<KybStatus, string>,
    flags: {
      vip: 'VIP',
    } satisfies Record<B2bCompanyFlag, string>,
    legal_form: {
      other: 'Other',
    },
  },
  actions: {
    edit: {
      title: 'Edit company',
      success: 'Company updated',
      failure: 'Company failed to be updated: %{error}',
      steps: {
        '1': 'Company Information',
        '2': 'Legal Representative',
        '3': 'Request',
      },
    },
  },
  dashboard: {
    active_campaigns: {
      title: {
        singular: 'Active operation',
        plural: 'Active operations',
      },
      operation_id: 'Operation ID',
      status: 'Status',
      amount: 'Amount',
      in_progress: 'In progress',
      answer_rate: 'Answer rate',
      of_affiliates_answered: 'of affiliates answered',
      no_active_campaigns: 'No active campaigns',
    },
  },
  sections: {
    company_info: {
      company: 'Company',
      logo: 'Company Logo',
      details: 'Company Details',
      address: 'Company Address',
      mailing_address: 'Mailing address',
      billing_address: 'Billing address',
      search: 'Enter company name or Siret number',
    },
    legal_representative: {
      details: "Company's legal representatives details",
      add: 'Add company representative',
      business_correspondent: {
        details: "Company's business correspondents details",
        add: 'Add business correspondent',
      },
      fields: {
        role: 'Role in company',
        firstname: 'First Name',
        lastname: 'Last Name',
        email: 'Email',
        error_email_domains:
          "The email address does not match one of the company's domain : %{domains}.",
        landline: 'Landline phone number',
        phone_number: 'Mobile phone number',
        is_business_correspondent: 'Is business correspondent',
      },
    },
  },
  employee_status: {
    current: 'Current',
    past: 'Past',
    retired: 'Retired',
  } satisfies Record<EmployeeStatus, string>,
  saving_plans: b2bCompaniesSavingPlansTranslations,
  documents: {
    button: 'Upload document',
    empty: 'This company has no documents yet',
    create: {
      title: 'Upload Company Document',
      success: 'Document Uploaded',
      failure: 'Error uploading document: %{error}',
      label: 'Upload Document',
    },
  },
  payment_methods: {
    button: 'Add payment method',
    empty: 'This company has no payment methods yet',
    create: {
      title: 'Create Payment method',
      success: 'Payment method added',
      failure: 'Error adding payment method: %{error}',
      label: 'Add Payment Method',
    },
    validation: {
      scan_sort_code_invalid: 'Invalid UK sort code format',
      scan_account_number_invalid: 'Invalid UK account number format',
      intl_account_number_is_iban: 'This looks like an IBAN. Please select the IBAN account type.',
      intl_sepa_zone_not_allowed: 'SEPA-zone banks must use the IBAN account type.',
    },
    delete_confirm: {
      title: 'Delete payment method',
      content: 'Are you sure you want to delete this payment method? This action cannot be undone.',
      shared_account_info:
        'This payment method shares the same mandate / bank account with another payment method. Only this payment method will be deleted, the mandate / bank account stays active for the other one.',
    },
    update: {
      title: 'Update',
      reason: 'Reason for temporary rejection',
      documents: 'Documents',
      subtitle: 'Please provide the missing document required to finalize your request',
      footnote:
        'Your SEPA direct debit payments are processed securely by Checkout SAS, an electronic money institution and payment service provider, authorized and regulated by the ACPR under number 17208, in accordance with its general terms and conditions and applicable regulations.',
      failure: 'Error updating payment method: %{error}',
    },
    title: 'General details',
    sections: {
      empty: {
        title: 'Payment method',
        no_payment_methods: 'You have not yet set up a payment method.',
      },
      payment_method: {
        title: 'Payment method',
        intent:
          'The designed bank account will be used to process payment for your financial transactions.',
        payment_method_type: 'Payment method type*',
        payment_method_intent: 'Payment method purpose*',
      },
      details: {
        title: 'Details',
        sepa_direct_debit: {
          title: 'Details',
          details: 'Add details to setup SEPA Direct Debit',
          description:
            'This SEPA Direct Debit will be used as the main mean of payment for the recovery of all payments relevant to your contract (Collective operations, matching contributions...)',
          invoicing_details: 'Add details to setup SEPA Direct Debit for invoicing',
          ccb_withdrawal_description: 'This SEPA Direct Debit will be used for CCB withdrawals.',
          payment_type: 'Payment type*',
          bic: 'BIC / SWIFT',
          iban: 'IBAN',
          rib: {
            title: 'RIB',
            upload: 'Upload a RIB for the account specified above in the name of %{company_name}',
          },
          add_second_payment_method: 'Add a different SEPA Direct Debit for Invoicing',
        },
        external_bank: {
          title: 'Details',
          details: 'Add details to link a bank account',
          description:
            'This bank account will be used as the main mean of payment for the recovery of all payments relevant to your contract (Collective operations, matching contributions...)',
          invoicing_details: 'Add details to link a bank account for invoicing',
          ccb_withdrawal_description: 'This bank account will be used for CCB withdrawals.',
          payment_type: 'Payment type*',
          bic: 'BIC / SWIFT',
          iban: 'IBAN',
          account_type: 'Account type',
          account_type_iban: 'IBAN account',
          account_type_intl: 'International account',
          account_type_scan: 'SCAN account (UK)',
          account_number: 'Account number',
          sort_code: 'Sort code',
          rib: {
            title: 'RIB',
            upload: 'Upload a RIB for the account specified above in the name of %{company_name}',
          },
        },
        checkout_disclaimer:
          'Your SEPA direct debit payments are processed securely by Checkout SAS, an electronic money institution and payment service provider, authorized and regulated by the ACPR under number 17208, in accordance with its general terms and conditions and applicable regulations.',
        signature_authorization: {
          title: 'Signature',
          question: 'Do you have the authorization to sign the SEPA Direct Debit mandate?',
          options: {
            self: 'Yes, I have the right',
            external_signatory: 'No, I am passing the signature to an authorized person',
          },
          hint: 'If you do not have authorization, we will ask you, in the next step, to mention the person who has signing authority.',
        },
      },
      signature: {
        title: 'Signature',
        billing_title: 'Billing information',
        iban: 'IBAN',
        bic: 'BIC / SWIFT',
        acknowledgements_title: 'Acknowledgements',
        subheader: 'SEPA Direct Debit mandate',
        sepa_mandate_body_start: 'By signing the',
        sepa_mandate_body_end:
          ', I authorize AXA Epargne Entreprise, via its service provider Checkout SAS, to debit my company’s bank account.',
        electronic_signature_note:
          'I understand I am signing these Acknowledgements electronically and that my electronic signature is the legal equivalent of my manual signature on these Acknowledgements.',
        sepa_mandate_link: {
          loading: 'Generating the document...',
          generate_failure: 'Error to generate the document',
          fetch_failure: 'Error to fetch the document',
        },
        external_signatory: {
          intro:
            'Add the details of the person authorised to sign the mandate, so that a signature link can be sent to them to complete the request.',
          firstname: 'First name',
          lastname: 'Last name',
          email: 'E-mail address',
        },
      },
      success_modal: {
        sepa_direct_debit: {
          title: 'Request sent',
          body: "It'll take a couple days to process your request. We'll keep you updated.",
          btn: 'Got it',
        },
        sepa_direct_debit_external_signatory: {
          title: 'Request sent',
          body: "An email containing the documents to be signed has been sent to the recipient you just mentioned. We'll keep you updated.",
          btn: 'Got it',
        },
        external_bank: {
          title: 'Bank account link initiated',
          body: 'Congratulations! Your bank account linking request has been successfully setup. It now needs to be verified before it is effective. You can follow its status in the Payment info tab.',
          btn: 'Got it',
        },
      },
    },
    items: {
      sepa_direct_debit: {
        principal: {
          title: 'SEPA Direct Debit - Financial operations',
        },
        invoicing: {
          title: 'SEPA Direct Debit - Invoicing',
        },
        ccb_withdrawal: {
          title: 'SEPA Direct Debit - CCB withdrawals',
        },
      },
      external_bank: {
        principal: {
          title: 'Bank account - Financial operations',
        },
        invoicing: {
          title: 'Linked bank account - Invoicing',
        },
        ccb_withdrawal: {
          title: 'Bank account - CCB withdrawals',
        },
      },
    },
    choices: {
      payment_types: {
        recurring: 'Recurring',
        one_off: 'One-off',
      } satisfies Record<PaymentMethodPaymentType, string>,
      statuses: {
        active: 'Active',
        deleted: 'Deleted',
        pending: 'Under review',
        rejected: 'Rejected',
        awaiting_customer: 'Awaiting customer',
        awaiting_signature: 'Awaiting external signature',
      } satisfies Record<PaymentMethodStatus, string>,
      payment_method_type: {
        sepa_direct_debit: 'Setup a SEPA Direct Debit',
        external_bank: 'Link a bank account',
      } satisfies Record<PaymentMethodType, string>,
      intents: {
        principal: 'Financial operations',
        invoicing: 'Billing',
        ccb_withdrawal: 'CCB withdrawals',
      } satisfies Record<PaymentMethodIntent, string>,
      retry_reasons: {
        RETRY_INCOMPLETE_DOCUMENT: 'Retry - Incomplete document',
        RETRY_DOCUMENT_NOT_SUPPORTED: 'Retry - Not supported document',
        RETRY_DOCUMENT_OLD_DOCUMENT: 'Retry - Old document',
        RETRY_POI_EXPIRED: 'Retry - POI expired',
        RETRY_POI_INCOMPLETE: 'Retry - POI incomplete',
        RETRY_POI_NOT_SUPPORTED: 'Retry - POI not supported',
        RETRY_POI_AND_RIB_CANNOT_BE_ACCEPTED: "Retry - POI and RIB can't be accepted",
        RETRY_RIB_INCOMPLETE: 'Retry - RIB incomplete',
        RETRY_NOT_SUPPORTED_DOCUMENT_NOT_RIB: 'Retry - Not supported document (not RIB)',
        RETRY_POOR_IMAGE_QUALITY: 'Retry - Poor image quality',
        RETRY_RANDOM_UPLOAD: 'Retry - Random upload',
        RETRY_OTHER: 'Retry',
      } satisfies Record<MdlrBankNameFailedTaskRetryReason, string>,
    },
    fields: {
      status: 'Status',
      retry_comment: 'Reason for retry',
      bank_name: 'Bank name',
      iban: 'Bank Account Number',
      beneficiary_name: 'Beneficiary name',
      created_at: 'Created on',
      updated_at: 'Modified on',
      payment_type: 'Payment type',
      mandate_unique_reference: 'Mandate unique reference',
      mandate_singed_at: 'Date of signature',
      mandate_signature_document_url: 'Mandate document',
    },
    actions: {
      create: {
        failure: 'Error creating payment method: %{error}',
      },
    },
  },
  admins: {
    choices: {
      role: {
        admin_level_1: 'Level 1 - Basic consultation',
        admin_level_2: 'Level 2 - Advanced consultation',
        admin_level_3: 'Level 3 - Consultation and Collective Operations only',
        admin_level_4: 'Level 4 - Admin',
      } satisfies Record<B2bAdminRoles, string>,
      language_preference: {
        FR: 'French',
        EN: 'English',
      } satisfies Record<B2bAdminsLanguagePreference, string>,
      status: {
        active: 'Active',
        inactive: 'Inactive',
        pending: 'Pending',
        invited: 'Invited',
      } satisfies Record<B2bAdminStatus, string>,
    },
  },
};
